import { HrmRequestFormResponse } from '@Types/api/form';
import { Status } from '@Components/Base';
import { CountryDict, TypeDict, User } from '@Types/index';
import { parseDateTime } from '@Utils/transformDateTime';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';
import { SponsorTypeCodes } from '@Enum/api/SponsorTypeCodes.enum';

import styles from './view-form.module.css';

const getSponsoredBySubfield = (
  sponsoredByCode: number,
  subfieldValue: string
): Record<string, string> | null => {
  if (sponsoredByCode === SponsorTypeCodes.COMPANY) {
    return {
      'Company Name': subfieldValue || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (sponsoredByCode === SponsorTypeCodes.SUB_CONTRACTOR) {
    return {
      'Sub-contractor Name': subfieldValue || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (sponsoredByCode === SponsorTypeCodes.SUPPLIER) {
    return {
      'Supplier Name': subfieldValue || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  return null;
};

const fieldSource = (
  data: HrmRequestFormResponse,
  findCountryType: (statusNum: number) => CountryDict | undefined,
  findStatus: (statusNum: number) => TypeDict | undefined,
  documentId: string,
  user: User | null,
  sponsors: TypeDict[],
  handleDownloadClick: () => void
): Array<{ label: string; value: string | number | boolean | JSX.Element }> => {
  const {
    leaveTypeID,
    travelDate,
    returnDate,
    totDays,
    destination,
    reqStatusId,
    filepath,
    remarks,
    eligibleDays,
    ticketRequired,
    batchNo,
  } = data;

  const statusLabel = findStatus(reqStatusId)?.codeDesc;
  const countryName = findCountryType(leaveTypeID)?.countryName;
  const empNumber = user?.empNumber;
  const name = user?.empName;
  const designation = user?.occupation;
  const currentLocation = user?.currentLocation;
  const dateOfTravel = parseDateTime(travelDate, 'DD/MM/YYYY');
  const returnDateValue = parseDateTime(returnDate, 'DD/MM/YYYY');
  const sponsoredBy = sponsors.find(
    (item) => item.codeNum === ticketRequired
  )?.codeDesc;

  const statusEl = <Status text={statusLabel || ''} />;
  const documentEl = documentId ? (
    <div className={styles.linkDownload} onClick={handleDownloadClick}>
      {filepath}
    </div>
  ) : (
    'No attachments'
  );

  const sponsoredBySubfield = getSponsoredBySubfield(ticketRequired, batchNo);

  const source = {
    Status: statusEl,
    'Employee Number': empNumber || EMPTY_FIELD_PLACEHOLDER,
    'Employee Name': name || EMPTY_FIELD_PLACEHOLDER,
    Designation: designation || EMPTY_FIELD_PLACEHOLDER,
    'Current Location': currentLocation || EMPTY_FIELD_PLACEHOLDER,
    'Place of Travel': destination || EMPTY_FIELD_PLACEHOLDER,
    'Date of Travel': dateOfTravel,
    'Return Date': returnDateValue,
    'Total No. of Days': totDays,
    'Purpose of Travel': remarks || EMPTY_FIELD_PLACEHOLDER,
    'Approved Travel Amount': eligibleDays || EMPTY_FIELD_PLACEHOLDER,
    Attachment: documentEl,
    Country: countryName || EMPTY_FIELD_PLACEHOLDER,
    'Sponsored by': sponsoredBy || EMPTY_FIELD_PLACEHOLDER,
    ...sponsoredBySubfield,
  };

  return Object.entries(source).map((item) => ({
    label: item[0],
    value: item[1],
  }));
};

export default fieldSource;
